
"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from 'react-redux';
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import promosTransactionService from '../apis/promos'
import { toast } from "react-hot-toast"
import moment from "moment-timezone"
import {
  getDefaultDateRangeThisMonth,
  formatCstDateRangeLabel,
  getCstNow,
  getCstMonthStart,
  getCstMonthEnd,
  CST_TIMEZONE,
  isDateTimeInBusinessRange,
  toCstMoment,
} from "../utils/cstDate"
import { InlinePromosAddBar } from "../components/inline-promos-add-bar"
import { PromosEditModal } from "../components/promos-edit-modal"
import { PromosDeleteModal } from "../components/promos-delete-modal"
import { PromosTransactionTable } from "../components/promos-transaction-table"
import DateRangePicker from '../components/ui/date-range-picker'
import { Calendar, Pencil } from "lucide-react"
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AdminEditModal from "../components/AdminEditModal"
import { useAuth } from "../context/AuthContext"
import { setPromosTransactionDetails } from '../Store/actions/transactionDetails'
import { isAdminRole } from "../constants/roles"

export default function PromosTransactions() {
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const transactionDetails = useSelector(state => state.transactionDetails.promosTransactionDetails);
  
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([])
  const [currentBalance, setCurrentBalance] = useState(0) // Store current balance separately
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [dateRange, setDateRange] = useState(() => {
    const { start, end } = getDefaultDateRangeThisMonth();
    return { start, end };
  });

  const [refreshFlag, setRefreshFlag] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [currentTransaction, setCurrentTransaction] = useState(null)
  const [mostRecentId, setMostRecentId] = useState(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [entriesPerPage, setEntriesPerPage] = useState(10)
  const [transactionDetailsUpdated, setTransactionDetailsUpdated] = useState(false);
  const [isAdminEditModalOpen, setIsAdminEditModalOpen] = useState(false);

  const isCashier = (currentUser?.role || user?.role || "").trim() === "Cashier";
  const currentMonthStart = useMemo(() => getCstMonthStart(), []);

  // Load promo catalog from server (shared across all staff)
  useEffect(() => {
    const loadPromoOptions = async () => {
      try {
        const response = await promosTransactionService.getPromoOptions();
        if (response.success) {
          const options = response.data?.options || response.data?.data?.options;
          if (Array.isArray(options) && options.length > 0) {
            dispatch(setPromosTransactionDetails(options));
            localStorage.setItem('transactionDetails', JSON.stringify(options));
            return;
          }
        }
      } catch (error) {
        console.error('Failed to load promo options from server:', error);
      }

      // Fallback to local cache / defaults
      const savedDetails = localStorage.getItem('transactionDetails');
      if (savedDetails) {
        try {
          dispatch(setPromosTransactionDetails(JSON.parse(savedDetails)));
        } catch (parseError) {
          console.error('Failed to parse cached promo options:', parseError);
        }
      }
    };

    loadPromoOptions();
  }, [dispatch]);

  // Initialize user from localStorage
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      setUser(null);
    }
  }, []);

  // Helper function to safely parse numeric values
  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const dateRangeSummary = useMemo(() => {
    const effectiveStart = isCashier && moment(dateRange.start).tz(CST_TIMEZONE).isBefore(currentMonthStart, "day")
      ? currentMonthStart
      : dateRange.start;

    const filteredByDateRange = transactions.filter(transaction => {
      if (isCashier && moment(transaction.date).tz(CST_TIMEZONE).isBefore(currentMonthStart, "day")) {
        return false;
      }
      return isDateTimeInBusinessRange(transaction.date, effectiveStart, dateRange.end);
    });

    const deposit = filteredByDateRange.reduce((sum, tx) =>
      tx.transaction_type === "deposit" ? sum + parseNumericValue(tx.transaction_amount) : sum, 0);

    const withdraw = filteredByDateRange.reduce((sum, tx) =>
      tx.transaction_type === "withdraw" ? sum + parseNumericValue(tx.transaction_amount) : sum, 0);

    return {
      deposit,
      withdraw,
      balance: currentBalance
    };
  }, [transactions, dateRange, currentBalance, isCashier, currentMonthStart]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      if (user.role === "Cashier") {
        const verificationRes = await promosTransactionService.checkVerificationStatus();
        if (verificationRes.success) {
          if (verificationRes.data.status === "unverified") {
            setLoading(false);
            return;
          }
        }
      }

      const [balanceRes, transactionsRes] = await Promise.all([
        promosTransactionService.getCurrentBalance(),
        promosTransactionService.getAllTransactions()
      ]);

      if (balanceRes.success && transactionsRes.success) {
        const rawBalance = parseNumericValue(balanceRes.data.balance);
        const roundedBalance = Math.max(0, Math.round(rawBalance));

        const transactionData = transactionsRes.data || [];

        // Sort transactions by date descending
        const sortedTransactions = [...transactionData].sort((a, b) =>
          new Date(b.date) - new Date(a.date)
        );

        setCurrentBalance(roundedBalance);
        setTransactions(sortedTransactions);

        // Set most recent transaction ID for deletion validation
        if (sortedTransactions.length > 0) {
          setMostRecentId(sortedTransactions[0].id);
        }
      } else {
        toast.error("Failed to load data");
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [user]); // Removed verificationStatus from dependencies

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user, refreshFlag, fetchData]); // Added fetchData to dependencies

  const openEditModal = (transaction) => {
    setCurrentTransaction(transaction);
    setIsEditModalOpen(true);
  };
  const openDeleteModal = (transaction) => {
    setCurrentTransaction(transaction);
    setIsDeleteModalOpen(true);
  };

  const handleAddTransaction = async (formData) => {
    try {
      const response = await promosTransactionService.addTransaction(formData);

      if (response.success) {
        toast.success(response.message || "Transaction added successfully");
        const newTransaction = response.data;

        // Add to beginning of transactions array
        setTransactions(prev => {
          const newTransactions = [newTransaction, ...prev];

          // Update most recent ID
          if (newTransactions.length > 0) {
            setMostRecentId(newTransactions[0].id);
          }

          return newTransactions;
        });

        // Immediately update current balance
        setCurrentBalance(parseNumericValue(newTransaction.after_balance));

        setRefreshFlag(prev => !prev);
        setCurrentPage(1);
        return true;
      } else {
        toast.error(response.error || "Transaction failed");
        return false;
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error("Transaction failed");
      return false;
    }
  };

  const handleUpdateTransaction = async (id, updatedData) => {
    try {
      const response = await promosTransactionService.updateTransaction(id, updatedData);

      if (response.success) {
        toast.success(response.message || "Transaction updated successfully");

        setTransactions(prev => prev.map(tx => {
          if (tx.id !== id) return tx;
          return {
            ...tx,
            player_name: updatedData.playerName ?? tx.player_name,
            notes: updatedData.notes ?? tx.notes,
            date: (updatedData.date && updatedData.date !== tx.date) ? toCstMoment(updatedData.date).toDate() : tx.date,
          };
        }));

        // Refresh current balance
        const balanceRes = await promosTransactionService.getCurrentBalance();
        if (balanceRes.success) {
          setCurrentBalance(parseNumericValue(balanceRes.data.balance));
        }
        setRefreshFlag(prev => !prev);
        return true;
      } else {
        toast.error(response.error || "Update failed");
        return false;
      }
    } catch (error) {
      console.error('Edit error:', error);
      toast.error("Update failed");
      return false;
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      const response = await promosTransactionService.deleteTransaction(id);

      if (response.success) {
        toast.success(response.message || "Transaction deleted successfully");

        // Update local state
        const deletedTx = transactions.find(tx => tx.id === id);
        if (deletedTx) {
          const isDeposit = deletedTx.transaction_type === "deposit";
          const amount = parseNumericValue(deletedTx.transaction_amount);

          setTransactions(prev => {
            const newTransactions = prev.filter(tx => tx.id !== id);

            // Update most recent ID if needed
            if (id === mostRecentId && newTransactions.length > 0) {
              setMostRecentId(newTransactions[0].id);
            }
            setRefreshFlag(prev => !prev);
            return newTransactions;
          });

          // Update current balance
          setCurrentBalance(prev =>
            isDeposit ? prev - amount : prev + amount
          );
        }

        return true;
      } else {
        toast.error(response.error || "Deletion failed");
        return false;
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Deletion failed");
      return false;
    }
  };

  const handleDateRangeApply = (startDate, endDate) => {
    let nextStart = startDate;
    let nextEnd = endDate;

    // Cashiers cannot view past-month data
    if (isCashier) {
      if (moment(nextStart).tz(CST_TIMEZONE).isBefore(currentMonthStart, "day")) {
        nextStart = currentMonthStart;
        toast.error("Cashiers can only view current month data");
      }
      if (moment(nextEnd).tz(CST_TIMEZONE).isBefore(currentMonthStart, "day")) {
        nextEnd = getCstMonthEnd();
      }
    }

    setDateRange({ start: nextStart, end: nextEnd });
    setIsDatePickerOpen(false);
    setCurrentPage(1);
  };

  const formatDateRange = () => {
    return formatCstDateRangeLabel(dateRange.start, dateRange.end);
  };

  // Filter transactions based on search term and date range
  const filteredTransactions = transactions
    .filter(transaction => {
      const matchesSearch = !searchTerm ||
        (transaction.player_name && transaction.player_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (transaction.notes && transaction.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (transaction.added_by && transaction.added_by.toLowerCase().includes(searchTerm.toLowerCase()));

      const effectiveStart = isCashier && moment(dateRange.start).tz(CST_TIMEZONE).isBefore(currentMonthStart, "day")
        ? currentMonthStart
        : dateRange.start;
      const matchesDateRange = isDateTimeInBusinessRange(transaction.date, effectiveStart, dateRange.end);
      const withinCashierWindow = !isCashier || !moment(transaction.date).tz(CST_TIMEZONE).isBefore(currentMonthStart, "day");

      return matchesSearch && matchesDateRange && withinCashierWindow;
    })
    .map(transaction => {
      const role = (currentUser?.role || user?.role || '').trim();
      return {
        ...transaction,
        // Admin can delete any entry; others only the latest
        isDeletable: isAdminRole(role) || transaction.id === mostRecentId
      };
    });

  if (!user) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
        </div>
      </div>
    );
  }

  const base64Logo = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANUAAABvCAMAAABMz3WiAAABfVBMVEVHcEzBwL45LyX59/X7+vhxXEb38+/9/PvNvKl6Ykk/Lht6X0Pax7P49O+tlXxVPSTayLSOc1f28eqzmn9gQyeafV/27+VxUzXIsJVWOB1wUzTr3s+rjGvBo4R4WTjMsZKVdlTdyK1nRyi/nnurimjq2b+AYD7WuZW6mHKXdlF0UjHDoXmvjGXZvpyMa0ZqSil3VzTkzapUNBmmhFzDnnSUdE2FZD9bOx1xUC7Mq4Tiy6PWuJC8mnRiQCCCXzp0US6lglzTsYbIpnyuimSZdE2/m3Hew5aQbUZyTixkQSKGZD99Wjavil6feE+YcUmlf1SJYz7StIPIp3a/oG+5mmq1lWWxkGGujV6riVuohVikgVWhfVKdeU6adkyXc0mUcEeRbUSPakKMZ0CjXDyKZT6JYz2HYjyFYDqDXjiBXDeRUDN+WTR7VjF4Ui91UC2aOiZyTSpuSSeIOyazJxtpRCSoHhVjPyCKKxyfHxafGBGYFxCTFhCOFA+HFA+AEQ1g9lSzAAAAVHRSTlMAAQMEBwkLEREQExkbHCAiJyYqLzE9QUBDR0pPUlVcYWJmaG1wdXV4hYiIlp6hoaCipaS1tri9vb7DxsfHz9HQ2Nva2tvd3uDh5Obn7Ozu8fD5+/4HuOOJAAAWkklEQVR42uzX3W9c13WG8edda5+h5KR1YicxHBtIAsRJW/Tjqr1q7/r/XxRIA6RB7Lb+0gcl0aI0M+fstd4ahGRRtkzY7hQICv+IwSE5wGCes/Zs7OF73/ve9773ve/9vyK4mcT/pcF1cXvwHWyHBkC3dnwz01vZvMLyGi883U5wV3Xrlz8URoA7ZAEWWARXOuBKEzz34L8PBuCtdxYQgHlB5iWCvT7bP31azXUvXuKZ7aO7/t/PyvHaL3dNh0x3pLVMdao8nCO97liRijNmbLkcI0Ol7d+SKxFvvefNGmFZSVXSke7Ewm2RbBNGke+ff3b+eG++JH729zzTf3yQ8wQrELw2YecGSNOW1VPB1uoqmbnztKKaaq0he9OIAjCuue7qeAZzZHumHJjpmdDYLPOoqIJ3ftn/fu/uxTQvkVZAZbH2CL6T4GXZ4PYE0dCRYSkxbJYbBbtwiMCD7rCSF4rUULV7ThiaTfVsJZOs8nFVBkF7zv17//zrt2/zMtO0QLZtOMms2gqaIUqhgRDKMCgdPbLtRtmjSOYSJfEFpUtTMbWcBRDduAdCJdw9ghpFp9uhqX948/2PPjMvCSh35lwDTjQrNUaepcC0rQjVdCojU62kXJ0iApxahnkhVE2x0CVByy6leoa2KivmNLVWRpYX1bu/euc1XtbHCKAYrRNVhUIBTbs7ol12R2S6Wqg22xUKNrq2pkf35BlDZCDl+FwRXdvWS1fPVm/TSE0pFBoDRUpv/+YXP+BlKUnAwCeqagVtG2EHUUjtOFOrq3j2ZqJhGWjnOdU8J1gPweahhpBnnu1cEEPTA2pfy6gWIuLsNZzoJ795Z+ElcqGQ+mQrEGyFZA2prBySgnYQChxackjEQGlH5m4Rzxg2N2P01j27N2VET2+blpSxmat2QkjytLD7x7/+aXBNqNsGjT5RVZBJK8lBqmwkBa6QHZ41C4VZwpZEaFy7pQLlFtQmOr15uBTabzWRZ+UiqRUkLR+qY1a33n33da4pxxBVXSc6MdFTttsWW7bDlFCHy6ixPMlJF8Xcna0Vc3doXhil9nCnfni4deDWgd3nD+BW3gY29jN7U3Y7hcSuwj9/dNF8QV0ZpqNDp6lSAVjacMNRSJ3QQoSYYY9pKkxsbtGbt4Uv2JFbQJzFLl6bsevPL2uMGTsGmGXbPICK0RVRRcwfvf76Q67pztbotk9RpX58BwAZZGRBB010QBM0ENBAFgkWdRlcMcwzY8fkTIhBMxfoOQDmkMdYHo+ZS3WKomEw3r73yDwXq1CxkOYUVd7uPeQ7mE8aAMGtpG6PufhwazFMYBvBiOYqrYIf8Njbrg3qzKnOeuMvb+15rrOD7KbjJFXUhQjouPqB61cgnj/x3PM/yjwzylZlzR3ILJ6kGWqNORTVO3Gbue+sjqiZi5vSzz46mGdaSERjTlPl4hVkAJlrBPCV/xfVgdsFXQPCi9rM4WCxewZe+eH+OHdYHs4wlb59O4pn1DnaeJyg6gYGwFznV/wG6T23s30ccwdzgRZzoWDtnewBRLIs3VHkgrs1evvRD7P9RZWdYoT4roKTqqruWFSGZQkaM6xUgCNDSEsyNLfOiTyPigwYO75Q+4kN9J9FlSoJmBP3sQgbTQUhNM7CtgXCLAo3nuWka3PfgiuCSMk1wSfYLU7AEDqc5cGRtAuInStKOKIhZLA32FFelkkWteWyMrhi6HGmtmH8mazABDO3ahoQ0BCEts2W5GoDu+UWG9k1sLVkhXjBHtmk232aWUl8AzZfT1GbmCYUDYAMAxrUCpugyUl2KTqIkMU1rhGdsnSaqvH6DrD4ghGAZQEWsF0ezCupcNuoibNdWKyLkbBrBPZc7MQIRskZdPS2UN4XX5j2HOD2aap2P3+XShnhojJVJEbPH2a99f62Fq/kxIpNRhBYQBhLGYI4Nt56kaNK7oiJGqri8Vx5Ljs1gHmy3eLsTXdkkdjYZOZcjnbSGRNax22t5tUMBJiwaEV7CdsBsjBLh7YthYkac4fcNI6tVswV0TGEPXYrJ6oSZVWrygoT2ROm8cBzqtTzg4ujeTWBWzK4oCVVD5vuAKk16B1y+EDvMKbVEX18+sQ84wpL6oJTVXlzS21ROVuLQdMCoE1yvLzcb3wNg4yAKOSW5BmNKhIRBgLs2Gi7I7B36m29NwuuWEEpWtbJzhZtq9buwqR6a1LOJai20pTvnT81X0MgOxEhrNgcMigW2W6wQnawbggqmA5ge3R40jyXENic7nQbatnARKDhzsiwaSGY2527T4obWCqghawBEtlbCGxLtix4nAkp20OdXZcXZZ7blJbsk1UJbJYKVdsGFyGXw4q+GtV+cpMoWgDHOQJjezGhLSINRoankznWDJO0an9+//rGLqBUA9dJVqCMgojMbkohV1fTDqWQZn9wcVl8LSPabRAAIctJ7GQaG9sOrXu7qa26sf1k/+nF3nwhRKPUiDzJrCySFpot0UgtV9qSJu66vLzY+HrCiMCyqNoJ9ZaNxWgDEvKTJ1eDS5Pt9OHw8YOL4gVPsLILdIpZYaaXQSMJHEKiIy1GePre+TRfz6DIgZRNYjvizGBEhCTE8fyxBsrKkSlxfHr/7mfTvKAgkxTgk1QJlz1JFOme7QlVeKu1uu7evFUgRKBIOs7yLEUra87Hx+MWq9bjk8vzB+tcSbkctDg8vffh+b64xh0KiuZEp1vI6SGDRm9NqUth2RB1+flN5WYuJFkYQJinj2OLFS+6ZLqHgppLmYroerrd//D8q69qB12hU1UpEUT2tO/smTwzJhwujsXNJLusVCMQclVExhxsHfRwHAhs5NZhPd65+5UohWR3A6eqIjuMlcC9c65sy7PrceNGxuGCDoPBYbPlGF6YuBW5xdkKnRXb2tuDh3c/e1q8LOyUSXy6qjI2ok1cbuY6mRsJ2pJMB1SEYYugYpStaM1w79q4+3g+H12eP1nNlxQTQtHrifZAKNONjPgqczOjsEZKEShCopbd4TBpz17CXcy5ueYM7z/5w3/+11dOygLBJFORcKKqwJvaKNp8W8KEbEyhVF8leowu5wBmr1BImbs33/jBcSu+xBAolVjgE1W1JYtZ5tszNGHkEI0lhM7ORlDREwo6czpnKX773ns/XfgqabSnR83TzSqS7kIO8W0JyVWGPEOWbSBLAUk1gu4lmNCht//pt2+/Jr7MBknFOFmVAiOHim/P5W7cMTRvhbANni6kjWC5vVsUDtop0f3Xf/fLN4PrAkbmwEme8PtVNiMB2XxrpQjJhX1ACgkI5qwcElUSdayOsRW5DP3qX3/91m3xQkMMQGCj0+zsUUABpvn2tvO3wOBpLAQCSZigS3YZNhYCQxX5j2ef3HncXLMZtDA7Tve5sgJjvpP1AC2kAEAILEUk3eHCZc87H2p29lS4mX/z3q/eWLgmRUa34mRniwZjEQQG8+3Mp1nOEEQCyM0IKlF5aubWMJ9+2r/G1IApePvN5U8PzHXyceTRuE9SxewQQhSaY/CN+Nhc2S4/+tmSG2nWdSdL82ii28SeQYmo+uxhvvFTyIlC3o6Hg6N4wUIjS47a7fhmtumvrXIsqwMTHaE3fhBNYGQBFgaEQWBhBFiPPj2aKxf336Jxo+3BXyywHdasYy9CGcfOaNX54cnxtbebyiBmHY7v33nSXNPRkNjvfvIG30jp0cfr11aJ3M0O5Hb6VxkoMxKo9m6DaDxkMecI7WY75vLk9/ePcGX/6OGPOyxhP7jlY/Q4duYoamJ1p9f1otZPfvdXbSqp4/E/Hlys5poQrYLgX6IJrqydJJUUSUAHTS1sGc2T3z/UTZ+rDdlZGBMuYWtNS/Qed4C3lEU5iH2nel01orjS9z7+2WoNPFFvRIaKIdWKVAof5919+cGnb/xEY1u1Ht9/cH81LwuCEnUUBKCctQ0qqgwVlVRqcxQx6fXR+b5uqApNFOUgZYHkltO9a4wisOwOdw77YJUD8cLF/buvSwGQGqJDhaq7CKTu452Lo9k+rw836+FPH31pUiBXdqYFBMhQkNFttRRVAuGgPbrDl3cv93XzOdC4DWHbBBJqYm6OyAFuKwQKU5LsCF7oO38KLb3RyBGN7XArb0d1uA6P7+034NGnH9Fe93+4+5WoMiKccoYBhK1cYCgEJQPQRqMJP1nvXTz1DVUNciPLchUyxEAqYbrd0KaK2spqEATXHD75Xa/O5mwng45eFi2pXFLute6d7w343vua6/5Pr1h+FNl0AQ2N0RAQEIiQMXSToUni48MHT4qbVuCw2sKJ5JAVoU5aSKaVtNxGYTeYQM01dfHxX75bO+aUjaZjUKZbyza3+cFHlxOA9e7vfnF4/+4roqzOhIoxozsYEFQp1dEB7qBRdIehfDh+up/c/K0RhyLSrYgQWEG6Q9AFWqJt4xGhCJPR5rrt/h8/9LZRHaOmbuHycTWadVw/uHuxPa//9OEfXhWFiLQjoMiEgiYzUlZAEWCD3VDlw0cPLoubzxZyY1qJF2PUUmFnNw41S4crbBlHEEZc5+2TefG3oCCkQZMrjvZ2uP/oWsa8q4cXr4jCzQRZJCw9XZCIAlNmANWJiGl0ePTgs40bq8IGQBltJCur1R1Lj2kZz2EHFY1Cahc2LynuzX7nJzWCcKBRUm9zrucf/k/71tYbx22Fv0MOd4d7mb2Odi2tbmtVlixBMWLXdYAkDRAXcXNpn/oU9LHoS/9p3logDwXaum5sK7K0uux9Zkh2xJmRR7aTuAUCJMB+i+Wcc3hIno8cYokzs+d5GpPHJjRvPvgTVyAOOCq8LMAj4hG4IUPEoECUbDKmEYwPT+fmhzJnBjCabBYaAFFElhvXYMqQ0UaR1kYTgQHGbrHXwsJgdl5baRAYKCSDSKkoOjmenYyC/Jp+xznTQPHIBkbKADpgUFZhiOyNRzZOZQgEzI5PpuEPvvWoWRIopbtOM2ZPBsYYnbw4qGORaSIeErM2JV6lpUdqNC5Xq3UTgM21DtXZi9n5eaTeJrcThA4zhl8WiGzeEIYcCpnRmpQRBKXIRsmg9cw+evj+/4n0l0QIAYRWTR9xJ6eUy0IBsZKeCq80c/bvc4Xr4CRFVXLpSmCE0WwyHb9dHqKzXuJWUM5Lf67SL2BjyWFy+GyO72UFx5P4PxC+adcTcwrCSaYoCudK4a3g1vn/NvZ0on6AFSPKazot8pbXjd/9ZoTdWIDRMHhb8FxIb5VhVQY/Agg/EljyWfxHb4EFFlhggQUW+EmARLlWllLGJWPyUmYAYEXBSrFeTr4s5wgAolSrSVmSDAlkuVEui6wmlkiU4kuiXTariayNyNoQi0HItFgVjOhaRXzJFGumK8/ESiwD5Vl5a/d3u61Wb3ffE43b93fqDgBcilt1ub2/2uo/2G31Dna98qXQisUDz7Jov3u/1/LX+jUGgLU3eq3G8loNILn87oONClF5ebfnkuy8+6DXavnb7yyTvPGrO37L7/dLydildr/vexwWTNb8VqPdLjFA1Nb6aw0BsNpyR5ZipWyjaq/5LoHJdqfRaHfaNYFSp99vx8pav81zvNjal/fqjuN29+oQt/+4V7R1JO98uVeQu7eq4vaf9ytub79C/T/dvXT091vWo/bod6uO23jnQQdgnfs3K5zL7Ts1AO0PPutxQPrdIk80xyl4uxucbvz2k5bjrn1wp5yM3fn1p1tFnsil/m634pbXtmssVt75y0G9QCC50SuK/qNH2wIAarvdIrHStvVcvrPrkeg8+rRXcb3Owa5LL3NMBiNEUTQ7uciyiokVUBqjb4cKIeLaGQyMdRydpe2mJq44G9Y7EtVNdTpSajpwV2RSqSBcPZgrABBQUXBxHNle4zbH4YpvI9DKvrBqg3G77SAIwtlJaVPChOFEK22A+Tgy0fMnN7s+AzAZzgy53bVJ7Dk/PuOMRYpzCqPJ6SB03pA5iyYRAIUr0KVthgTzc2ux4nSOlzBTfqPEWt3xCADG0w0vbcsqbBggg6172eaGy/PvWtplWFXDcaD0dLKywoyNzuZR9Eyb6OSrvV41+XOMij1Pp+NQqXA4TXmYKJ61GV5jRYLGAQCNPPQ4zAIZRoADgAnEjjloVRDVJR0qyzhca4qk8yq75idYMNbptBFmhl1PYJDb+MUsigCjR6hLQgbS2gDBN1/H+ytJrrhLl54GxozHSG4pEGPhNLiejRGAUz+bkR0wD6VNytTMAXAGFMvnc+TB+CQQdQTaajOnIkPAoLl0NINFCMCwUjRWBinh2uNXkx0kvJkKrRgMu89mQJhPlWP0vLI1mmsokJBh6hkOslUgiZnS19ZKh065U+Mw11lRNpXGpIWJOflNbvUMosEOQ455YtQgCADkLD1ckqkHgHJzpQTbDVij2xr8a/AKKyPqF2E6M9RwOcCzKCyCF0/XerXU89R6EjNRZAAEBm6rhFCZPCsmXDdZOIUczGuC1oIzneNtZNmXj58P863Klokz+vumT0jhcjJXMmORHgW5kK9LCAGHru4knV4unn+93JVQV3ThVH2/KggoFqvVepHDIM8K4WhwfAYQeH4QzV8fcTQ8HOUMxVKJHz0+z2e0zDDZYEdP+sseUszGgyNNZGOcnB0OvM1yNl8cqTRFCoHIWHIWLCsHh2Hf5/mpF5vrVW4ARMH5afSGpwdmfhYJB8j4ugSQepWVBkx4FgonswTD82+fDRXC0wLShtE8sjfF7OlXd1ck0vC0Gg7JTQZU0Xi60YBFFiNzwnk6OAHngSVnYdLHPjp68c1GtwaFcOJZTxUWvEgDmOggGs60k01AnlZUkLk14LlqgxzUTFckMgRBoOzPSJgw5eJ5GMDi4tk/NhsEQLDEs+gyZPBdTshBloKLViE7ws0DAKyYkGScpR0e/u3OUgGYHPNCGlaUNIZRoxGVeP5XOLuUBACe3vvXyBDyoEIRryA8GnquPR/IwYsQFub4SX/NAxBqWLACUkRgDncIGWLVjU6elZgBwKU4nCo1+w9jOhva4QZGDw6PNnyO8OTQehLIuQpPRY6b/xUmDQu3LTICTNbYFSuOFJnFrSVCnvjgSdEDwGvh01FmnBz99ZcrMlsrsFqNIRFV6BAvJDKHBvGypPMn1SpArFA5GYVKXwybdrGKLADIBhE9/Wez6jBcJJ4QFaTQBqxQNAwZSG795r26LDc23u/y8u7ne54s+3d33MbB57cqBBLWJggkt7+414odt++uAiDRvv/xqkvZftrYashyZ2e9CIjOhx+tCiJ5+9OHGzLWvujJcnn5YKcglj/5uCuA5Q8P7KGWOg//cKsgZPtgq+jUd3YaQpQ29jebDkhsfLTfkKLc8grEatvWm/sf3WtycOvJStsP318psM5nv1+Pu6htH+Ty2eR2NzfrntdaXa9yr7u5Uvda3fWu661urlQ4yLU2l8C87vp64li3SxYL3YwVUKi3YtQLAGRSQ9JfX+96VvNsp45sxV24BLd3s+sCkP7ee/HgfsykyAv11V7D83srzQIAp76+t9Xz4x45hL9lRyL35q0mB8Weqy3P39qM+Ut//95mq9HY2N1p5lgR59xJvsRTZCIBVwKQOGUarIYMjLjjpKr1Sfyda51SbLGtecFuK6fSXGpWXLferBZATrFScSvVogMg1qrNer1SKXCAu/VqgRJbkQCeelarRS4qzeZlF/GlSD+NdDqIkwUnAkAWuUqe2JGZr2opQepFoMuSFjmABRZYYIEFfjb4L2/bnLmPQWqLAAAAAElFTkSuQmCC'

  const exportPromosToPDF = () => {
    try {
      const doc = new jsPDF();

      const logoWidth = 40;
      const logoHeight = 20;
      doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight);

      doc.setProperties({
        title: 'Promos Transactions Report',
        subject: 'Promos Transactions',
        author: 'Elite Poker Lounge',
        creator: 'Promos System'
      });

      const now = getCstNow().toDate();
      const dateTime = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date & Time: ${dateTime}`, 14, 15);
      doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text('Promos Transactions Report', 105, 40, { align: 'center' });

      // Overall summary
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text('Summary:', 14, 50);

      doc.setFont(undefined, 'normal');
      doc.text(`Total Deposit: $${formatCurrency(dateRangeSummary.deposit)}`, 14, 60);
      doc.text(`Total Withdraw: $${formatCurrency(dateRangeSummary.withdraw)}`, 14, 70);
      doc.text(`Current Balance: $${formatCurrency(currentBalance)}`, 14, 80);

      // Promotion-wise totals (group by Promotion Details / notes)
      const promotionTotals = {};
      filteredTransactions.forEach((tx) => {
        const key = (tx.notes || '').trim() || 'Unspecified';
        if (!promotionTotals[key]) {
          promotionTotals[key] = { withdraw: 0, deposit: 0 };
        }
        const amount = parseNumericValue(tx.transaction_amount);
        if (tx.transaction_type === 'deposit') {
          promotionTotals[key].deposit += amount;
        } else if (tx.transaction_type === 'withdraw') {
          promotionTotals[key].withdraw += amount;
        }
      });

      const promotionEntries = Object.entries(promotionTotals).sort((a, b) =>
        a[0].localeCompare(b[0])
      );

      let yPos = 92;
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.text('Promotion Wise Transaction:', 14, yPos);
      yPos += 8;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);

      if (promotionEntries.length === 0) {
        doc.text('No promotion transactions in this range.', 14, yPos);
        yPos += 8;
      } else {
        promotionEntries.forEach(([promoName, totals], index) => {
          const line = `${index + 1}. ${promoName}: Total Withdraw = $${formatCurrency(totals.withdraw)} | Total Deposit = $${formatCurrency(totals.deposit)}`;
          const wrapped = doc.splitTextToSize(line, 180);

          if (yPos + wrapped.length * 5 > 280) {
            doc.addPage();
            yPos = 20;
          }

          doc.text(wrapped, 14, yPos);
          yPos += wrapped.length * 5 + 2;
        });
      }

      yPos += 6;

      const tableHeaders = [
        'Date',
        'Promos Balance',
        'Transaction',
        'Promotion Details',
        'Player Name',
        'After Balance',
        'Added By'
      ];

      const tableData = filteredTransactions.map(transaction => {
        const transactionAmount = transaction.transaction_type === 'deposit'
          ? `+${formatCurrency(transaction.transaction_amount)}`
          : `-${formatCurrency(transaction.transaction_amount)}`;

        return [
          moment(transaction.date).format("MM/DD/YYYY hh:mm A"),
          `$${formatCurrency(transaction.before_balance)}`,
          transactionAmount,
          transaction.notes || 'N/A',
          transaction.player_name || 'N/A',
          `$${formatCurrency(transaction.after_balance)}`,
          transaction.added_by || 'N/A'
        ];
      });

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: yPos,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 25 },
          2: { cellWidth: 20 },
          3: { cellWidth: 40 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 30 }
        },
        margin: { left: 14, right: 14 },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1
      });

      const fileName = `Promos_Report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
      doc.save(fileName);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF. Please try again.');
    }
  };

  // Add this handler function
  const handleAdminEditSave = async (updatedDetails) => {
    try {
      const response = await promosTransactionService.savePromoOptions(updatedDetails);
      if (!response.success) {
        toast.error(response.error || 'Failed to save promo list');
        return;
      }

      const options = response.data?.options || updatedDetails;
      dispatch(setPromosTransactionDetails(options));
      localStorage.setItem('transactionDetails', JSON.stringify(options));
      setTransactionDetailsUpdated(prev => !prev);
      toast.success('Promo list updated for all staff');
    } catch (error) {
      console.error('Failed to save promo options:', error);
      toast.error('Failed to save promo list');
    }
  };


  // Update the Admin/edit button click handler
  const handleAdminEditClick = () => {
    setIsAdminEditModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="flex justify-between items-center w-full mb-8">
          <div className="text-center flex-1">
            <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
              Promos Transactions
            </h1>
            <p className="text-gray-600 mt-2 text-sm">
              Manage and track promotional transactions
            </p>
          </div>
          {currentUser?.role?.trim() === "Admin" && (
            <div
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 px-3 py-1.5 rounded-md border border-gray-300 hover:border-blue-500 shadow-sm transition-colors duration-200 cursor-pointer"
              onClick={handleAdminEditClick}
            >
              <Pencil className="w-4 h-4" />
              <span className="font-medium">Admin</span>
            </div>
          )}


        </div>



        {/* Summary row: date range first, then metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                Date Range
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => setIsDatePickerOpen(true)}
                  className="w-full justify-start text-left font-normal"
                >
                  <Calendar className="mr-2 h-4 w-4 shrink-0" />
                  <span className="truncate">{formatDateRange()}</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                Total Deposit
                <span className="block text-xs text-gray-400 normal-case mt-1">
                  ({formatDateRange()})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  ${formatCurrency(dateRangeSummary.deposit)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                Total Money Withdraw
                <span className="block text-xs text-gray-400 normal-case mt-1">
                  ({formatDateRange()})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  ${formatCurrency(dateRangeSummary.withdraw)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                Current Balance
                <span className="block text-xs text-gray-400 normal-case mt-1">
                  (Live Balance)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  ${formatCurrency(currentBalance < 0 ? 0 : currentBalance)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Inline Add Bar */}
        <InlinePromosAddBar
          key={transactionDetailsUpdated}
          onAddTransaction={handleAddTransaction}
          transactionDetails={transactionDetails}
          currentBalance={currentBalance}
        />

        {/* Transaction History */}
        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">
              Transaction History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-3 mb-6">
              <Button
                onClick={exportPromosToPDF}
                variant="outline"
                className="bg-yellow-400 hover:bg-yellow-500 text-black"
              >
                Export to PDF
              </Button>

              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Search:
              </label>
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search transactions..."
                className="w-64"
              />
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="p-6 bg-gray-50 min-h-screen">
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                  </div>
                </div>
              </div>
            ) : (
              <PromosTransactionTable
                transactions={filteredTransactions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                entriesPerPage={entriesPerPage}
                setEntriesPerPage={setEntriesPerPage}
                onEdit={openEditModal}
                onDelete={openDeleteModal}
                dateRange={dateRange}
                currentUser={currentUser}
              />
            )}
          </CardContent>
        </Card>



        <AdminEditModal
          isOpen={isAdminEditModalOpen}
          onClose={() => setIsAdminEditModalOpen(false)}
          onSave={handleAdminEditSave}
        />

        {/* Edit Modal */}
        <PromosEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          transaction={
            transactions.find((t) => t.id === currentTransaction?.id) ||
            currentTransaction
          }
          onUpdateTransaction={handleUpdateTransaction}
          currentUser={user}
          transactionDetails={transactionDetails}
        />

        {/* Delete Modal */}
        <PromosDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          transaction={currentTransaction}
          onDeleteTransaction={handleDeleteTransaction}
        />

      {/* Date Range Picker */}
      {isDatePickerOpen && (
        <DateRangePicker
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange.start}
          initialEndDate={dateRange.end}
          initialSelectedRange="thisMonth"
          allowedRangeKeys={
            isCashier
              ? ["today", "yesterday", "thisWeek", "thisMonth", "custom"]
              : null
          }
          minDate={isCashier ? currentMonthStart : null}
        />
      )}
    </div>
  );
}